import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Alert,
  Button,
  Card,
  Image,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Steps,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  CheckCircleFilled,
  FileExcelOutlined,
  FileTextOutlined,
  InboxOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { assignCatalogToAccount, listContractorAccounts, matchPartNumbers, saveCatalog } from '../../services/contractorsHubApi';
import { useProductImageBaseUrl } from './useProductImageBaseUrl';
import { buildProductImageUrl } from '../../pages/orderXV4Utils';
import { parsePrice, parseEntries, detectColumnMap, entriesFromFile } from './catalogImportUtils';

const { Dragger } = Upload;

const { TextArea } = Input;
const { Text } = Typography;

const parseSpreadsheet = async (file) => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', raw: false });
  const sheetName = workbook?.SheetNames?.[0];
  if (!sheetName) return { headers: [], dataRows: [] };
  const sheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });
  const nonEmptyRows = rows.filter((row) => row.some((cell) => String(cell || '').trim()));
  if (!nonEmptyRows.length) return { headers: [], dataRows: [] };
  const [headerRow, ...dataRows] = nonEmptyRows;
  const headers = headerRow.map((cell) => String(cell || '').trim());
  return { headers, dataRows };
};

const UploadPartNumbersModal = ({ open, onClose, onSaved }) => {
  const [step, setStep] = useState('input'); // 'input' | 'review'
  const [inputMode, setInputMode] = useState('paste'); // 'paste' | 'file'
  const [rawInput, setRawInput] = useState('');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [colMap, setColMap] = useState({ partNumber: null, listPrice: null, cost: null });
  const [matching, setMatching] = useState(false);
  const [matched, setMatched] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [catalogKey, setCatalogKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [contractorAccounts, setContractorAccounts] = useState([]);
  const imageBaseUrl = useProductImageBaseUrl();
  const [contractorAccountId, setContractorAccountId] = useState(undefined);

  const reset = () => {
    setStep('input');
    setInputMode('paste');
    setRawInput('');
    setFileName('');
    setHeaders([]);
    setDataRows([]);
    setColMap({ partNumber: null, listPrice: null, cost: null });
    setMatched([]);
    setUnmatched([]);
    setCatalogKey('');
    setDisplayName('');
    setContractorAccountId(undefined);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (open) {
      listContractorAccounts()
        .then(setContractorAccounts)
        .catch(() => setContractorAccounts([]));
    }
  }, [open]);

  const handleFileSelected = async (file) => {
    try {
      const { headers: parsedHeaders, dataRows: parsedRows } = await parseSpreadsheet(file);
      if (!parsedHeaders.length || !parsedRows.length) {
        message.error('Could not find a header row and data in that file');
        return false;
      }
      setFileName(file.name || '');
      setHeaders(parsedHeaders);
      setDataRows(parsedRows);
      setColMap(detectColumnMap(parsedHeaders));
    } catch (err) {
      message.error(err.message || 'Could not read that file');
    }
    return false; // prevent antd from trying to upload it anywhere
  };

  const handleLookup = async () => {
    const { partNumbers, prices } =
      inputMode === 'file' ? entriesFromFile(dataRows, colMap) : parseEntries(rawInput);
    if (inputMode === 'file' && (colMap.partNumber === null || colMap.partNumber === undefined)) {
      message.error('Select which column contains the part number');
      return;
    }
    if (!partNumbers.length) {
      message.error('Enter at least one part number');
      return;
    }
    setMatching(true);
    try {
      const result = await matchPartNumbers(partNumbers, prices);
      setMatched(result.matched.map((m) => ({ ...m, key: m.sku })));
      setUnmatched(result.unmatched);
      setStep('review');
    } catch (err) {
      message.error(err.message || 'Could not look up part numbers');
    } finally {
      setMatching(false);
    }
  };

  const handleListPriceChange = (sku, list_price) => {
    setMatched((rows) => rows.map((r) => (r.sku === sku ? { ...r, list_price } : r)));
  };

  const handleCustomerPriceChange = (sku, customer_price) => {
    setMatched((rows) => rows.map((r) => (r.sku === sku ? { ...r, customer_price } : r)));
  };

  const handleCreate = async () => {
    if (!catalogKey.trim() || !displayName.trim()) {
      message.error('Catalog Key and Display Name are required');
      return;
    }
    if (!matched.length) {
      message.error('No matched items to save -- none of these part numbers were found in your product catalog');
      return;
    }
    setSaving(true);
    try {
      const result = await saveCatalog({
        catalogKey,
        displayName,
        items: matched.map(
          ({
            sku,
            name,
            list_price,
            customer_price,
            description,
            image_url: imageUrl,
            manufacturer_name: manufacturerName,
            manufacturer_part_number: manufacturerPartNumber,
            category,
            attributes,
            erp_description: erpDescription,
            inventory_qty_on_hand: inventoryQtyOnHand,
            inventory_is_available: inventoryIsAvailable,
          }) => ({
            sku: String(sku ?? ''),
            name: String(name ?? ''),
            list_price: parsePrice(list_price),
            customer_price: parsePrice(customer_price),
            description: String(description ?? '') || String(name ?? ''),
            image_url: String(imageUrl ?? ''),
            manufacturer_name: String(manufacturerName ?? ''),
            manufacturer_part_number: String(manufacturerPartNumber ?? ''),
            category: String(category ?? ''),
            attributes: attributes && typeof attributes === 'object' ? attributes : {},
            erp_description: String(erpDescription ?? ''),
            inventory_qty_on_hand: Number(inventoryQtyOnHand) || 0,
            inventory_is_available: Boolean(inventoryIsAvailable),
          }),
        ),
      });
      if (contractorAccountId) {
        await assignCatalogToAccount(contractorAccountId, result.master_id);
      }
      message.success(`Catalog created with ${matched.length} item(s)`);
      reset();
      onSaved();
    } catch (err) {
      message.error(err.message || 'Could not save catalog');
    } finally {
      setSaving(false);
    }
  };

  const colOptions = headers.map((header, idx) => ({ value: idx, label: header || `Column ${idx + 1}` }));
  const previewRows = dataRows.slice(0, 5);

  return (
    <Modal
      title="Import Items"
      open={open}
      onCancel={handleClose}
      width={760}
      footer={
        step === 'input'
          ? [
              <Button key="cancel" onClick={handleClose}>
                Cancel
              </Button>,
              <Button key="lookup" type="primary" onClick={handleLookup} loading={matching}>
                Look Up
              </Button>,
            ]
          : [
              <Button key="back" onClick={() => setStep('input')}>
                Back
              </Button>,
              <Button key="create" type="primary" onClick={handleCreate} loading={saving} disabled={!matched.length}>
                Create Catalog ({matched.length} item{matched.length === 1 ? '' : 's'})
              </Button>,
            ]
      }
    >
      <Steps
        size="small"
        current={step === 'input' ? 0 : 1}
        items={[{ title: 'Add items' }, { title: 'Review & save' }]}
        style={{ marginBottom: 24 }}
      />

      {step === 'input' && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            <Card
              hoverable
              size="small"
              onClick={() => setInputMode('paste')}
              style={{
                flex: 1,
                cursor: 'pointer',
                borderColor: inputMode === 'paste' ? '#1677ff' : undefined,
                background: inputMode === 'paste' ? '#f0f7ff' : undefined,
              }}
              styles={{ body: { display: 'flex', alignItems: 'center', gap: 10, padding: 14 } }}
            >
              <FileTextOutlined style={{ fontSize: 20, color: inputMode === 'paste' ? '#1677ff' : '#8c8c8c' }} />
              <div>
                <Text strong>Paste List</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Type or paste part numbers
                  </Text>
                </div>
              </div>
              {inputMode === 'paste' && <CheckCircleFilled style={{ marginLeft: 'auto', color: '#1677ff' }} />}
            </Card>
            <Card
              hoverable
              size="small"
              onClick={() => setInputMode('file')}
              style={{
                flex: 1,
                cursor: 'pointer',
                borderColor: inputMode === 'file' ? '#1677ff' : undefined,
                background: inputMode === 'file' ? '#f0f7ff' : undefined,
              }}
              styles={{ body: { display: 'flex', alignItems: 'center', gap: 10, padding: 14 } }}
            >
              <FileExcelOutlined style={{ fontSize: 20, color: inputMode === 'file' ? '#1677ff' : '#8c8c8c' }} />
              <div>
                <Text strong>Upload Excel / CSV</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Drag & drop or browse a spreadsheet
                  </Text>
                </div>
              </div>
              {inputMode === 'file' && <CheckCircleFilled style={{ marginLeft: 'auto', color: '#1677ff' }} />}
            </Card>
          </div>

          {inputMode === 'paste' && (
            <>
              <Text type="secondary">
                One part number per line. Optionally add list price and customer price on the same line (comma or
                tab separated) to import them directly -- otherwise both default to 0 and can be edited before
                saving. Each part number is matched against your product catalog to fill in the name automatically.
              </Text>
              <TextArea
                style={{ marginTop: 12 }}
                rows={10}
                value={rawInput}
                onChange={(e) => setRawInput(e.target.value)}
                placeholder={'PUMP-100, 129.99, 149.99\nRF-410-25, 45.00, 59.00\nTH-9001'}
              />
            </>
          )}

          {inputMode === 'file' && (
            <>
              {!fileName ? (
                <Dragger
                  accept=".csv,.xlsx,.xls"
                  showUploadList={false}
                  multiple={false}
                  beforeUpload={handleFileSelected}
                  style={{ background: '#fafafa' }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ color: '#1677ff' }} />
                  </p>
                  <p className="ant-upload-text">Click or drag a spreadsheet to this area</p>
                  <p className="ant-upload-hint">Supports .xlsx, .xls and .csv with a header row</p>
                </Dragger>
              ) : (
                <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
                  <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <FileExcelOutlined style={{ fontSize: 20, color: '#52c41a' }} />
                      <div>
                        <Text strong>{fileName}</Text>
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {dataRows.length} row{dataRows.length === 1 ? '' : 's'} found
                          </Text>
                        </div>
                      </div>
                    </Space>
                    <Upload accept=".csv,.xlsx,.xls" showUploadList={false} beforeUpload={handleFileSelected}>
                      <Button size="small" icon={<ReloadOutlined />}>
                        Change file
                      </Button>
                    </Upload>
                  </Space>
                </Card>
              )}

              {headers.length > 0 && (
                <>
                  <Card size="small" title="Map columns" style={{ marginBottom: 16 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                      Obvious headers (e.g. "SKU", "List Price", "Cost") are mapped automatically -- adjust below if
                      needed.
                    </Text>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Space>
                        <Text style={{ width: 120, display: 'inline-block' }}>Part Number *</Text>
                        <Select
                          style={{ width: 260 }}
                          placeholder="Select column"
                          options={colOptions}
                          value={colMap.partNumber}
                          onChange={(value) => setColMap((prev) => ({ ...prev, partNumber: value }))}
                        />
                      </Space>
                      <Space>
                        <Text style={{ width: 120, display: 'inline-block' }}>List Price</Text>
                        <Select
                          style={{ width: 260 }}
                          placeholder="Select column (optional)"
                          allowClear
                          options={colOptions}
                          value={colMap.listPrice}
                          onChange={(value) => setColMap((prev) => ({ ...prev, listPrice: value ?? null }))}
                        />
                      </Space>
                      <Space>
                        <Text style={{ width: 120, display: 'inline-block' }}>Cost</Text>
                        <Select
                          style={{ width: 260 }}
                          placeholder="Select column (optional)"
                          allowClear
                          options={colOptions}
                          value={colMap.cost}
                          onChange={(value) => setColMap((prev) => ({ ...prev, cost: value ?? null }))}
                        />
                      </Space>
                    </Space>
                  </Card>

                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    Preview (first {previewRows.length} of {dataRows.length} rows)
                  </Text>
                  <Table
                    size="small"
                    bordered
                    pagination={false}
                    rowKey={(_, idx) => idx}
                    scroll={{ x: true }}
                    dataSource={previewRows.map((row, idx) => ({ idx, row }))}
                    columns={headers.map((header, colIdx) => ({
                      title: header || `Column ${colIdx + 1}`,
                      dataIndex: ['row', colIdx],
                      render: (val) => String(val ?? ''),
                    }))}
                  />
                </>
              )}
            </>
          )}
        </>
      )}
      {step === 'review' && (
        <>
          <Space style={{ marginBottom: 16 }} wrap>
            <Input placeholder="Catalog Key (e.g. hvac-standard)" value={catalogKey} onChange={(e) => setCatalogKey(e.target.value)} style={{ width: 240 }} />
            <Input placeholder="Display Name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={{ width: 240 }} />
            <Select
              placeholder="Associate with contractor (optional)"
              allowClear
              style={{ width: 260 }}
              value={contractorAccountId}
              onChange={setContractorAccountId}
              options={contractorAccounts.map((a) => ({ value: a.id, label: a.display_name }))}
            />
          </Space>
          {unmatched.length > 0 && (
            <Alert
              type="warning"
              style={{ marginBottom: 16 }}
              message={`${unmatched.length} part number(s) not found in your product catalog`}
              description={unmatched.map((code) => <Tag key={code}>{code}</Tag>)}
            />
          )}
          <Table
            size="small"
            rowKey="sku"
            pagination={false}
            dataSource={matched}
            columns={[
              {
                title: 'Image',
                dataIndex: 'image_url',
                width: 72,
                render: (url) => {
                  const resolved = buildProductImageUrl(url, imageBaseUrl);
                  return resolved ? (
                    <Image src={resolved} alt="" width={40} height={40} style={{ objectFit: 'contain' }} />
                  ) : (
                    <div style={{ width: 40, height: 40, background: '#f0f0f0', borderRadius: 4 }} />
                  );
                },
              },
              { title: 'SKU', dataIndex: 'sku' },
              { title: 'Name', dataIndex: 'name' },
              {
                title: 'List Price',
                dataIndex: 'list_price',
                render: (price, row) => (
                  <InputNumber
                    min={0}
                    step={0.01}
                    value={price}
                    onChange={(value) => handleListPriceChange(row.sku, value)}
                  />
                ),
              },
              {
                title: 'Customer Price',
                dataIndex: 'customer_price',
                render: (price, row) => (
                  <InputNumber
                    min={0}
                    step={0.01}
                    value={price}
                    onChange={(value) => handleCustomerPriceChange(row.sku, value)}
                  />
                ),
              },
            ]}
          />
        </>
      )}
    </Modal>
  );
};

export default UploadPartNumbersModal;
